/**
 * Payments > Online payments (tracker item 38): every online charge attempt
 * (pay link, autopay, card on file) with the counts that need attention:
 * charged but not recorded (RECORD_FAILED), outcome unknown (ERROR), needs
 * review (recorded as credit), refunded at the processor / void pending.
 * Reconcile re-checks the processor and records a missed charge; an unknown
 * outcome can be closed as not charged after checking the processor
 * dashboard; a refund whose void is pending can be finished (ADMIN).
 * ADMIN and OFFICE_MANAGER.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IOnlinePaymentsApi } from '../api';
import * as types from '../api/types';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/apiError';
import { canRefundOnlinePayments } from '../utils/permissions';
import { inputClass, labelClass, rowActionClass, rowDangerActionClass, secondaryButtonClass } from '../components/formStyles';
import { AttemptStatusBadge, RefundForm } from '../features/online-payments/OnlinePaymentWidgets';
import {
  ATTEMPT_SOURCE_LABELS,
  ATTEMPT_STATUS_LABELS,
  attentionReason,
  cardLabel,
  formatUsd,
  refundPendingVoid,
} from '../features/online-payments/onlinePaymentsUi';
import { Toast } from '../components/Toast';

const PAGE_SIZE = 50;

export function OnlinePaymentsPage() {
  const api = useMemo<IOnlinePaymentsApi>(() => createAPIClient(), []);
  const { user } = useAuth();
  const [status, setStatus] = useState<types.OnlinePaymentAttemptStatus | ''>('');
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [list, setList] = useState<types.OnlinePaymentAttemptList | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [closeFor, setCloseFor] = useState<string | null>(null);
  const [refundFor, setRefundFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setList(await api.listOnlinePaymentAttempts({ status, needsAttention: attentionOnly || undefined }, page, PAGE_SIZE));
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not load online payments'));
    } finally {
      setLoading(false);
    }
  }, [api, status, attentionOnly, page]);

  useEffect(() => { void load(); }, [load]);

  async function reconcile(attempt: types.OnlinePaymentAttempt, resolution?: 'NOT_CHARGED') {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const after = await api.reconcileOnlinePaymentAttempt(attempt.id, resolution);
      setNotice(
        resolution
          ? 'Closed as not charged.'
          : after.status === 'RECORDED'
            ? 'The charge is recorded.'
            : `Checked: ${ATTEMPT_STATUS_LABELS[after.status].toLowerCase()}.`
      );
      setCloseFor(null);
      await load();
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not reconcile'));
    } finally {
      setBusy(false);
    }
  }

  const summary = list?.summary;
  const pages = Math.max(1, list?.pagination.pages ?? 1);
  const counts: [string, number | undefined, string][] = [
    ['Charged, not recorded', summary?.recordFailed, 'RECORD_FAILED'],
    ['Outcome unknown', summary?.outcomeUnknown, 'ERROR'],
    ['Needs review', summary?.needsReview, ''],
    ['Refunded at processor', summary?.refundedAtProcessor, ''],
    ['Void pending', summary?.voidPending, ''],
  ];

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/payments" className="text-sm text-blue-700 hover:underline">← Payments</Link>
          <h1 className="text-2xl font-bold mt-1">Online payments</h1>
        </div>
        <button type="button" className={secondaryButtonClass} disabled={loading} onClick={() => void load()}>Refresh</button>
      </div>

      <section aria-label="Needs attention" className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {counts.map(([label, count, filter]) => (
          <button
            key={label}
            type="button"
            className={`rounded-lg border p-3 text-left ${count ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'}`}
            onClick={() => { setPage(1); if (filter) { setStatus(filter as types.OnlinePaymentAttemptStatus); setAttentionOnly(false); } else { setStatus(''); setAttentionOnly(true); } }}
          >
            <span className="block text-2xl font-bold" data-testid={`count-${label}`}>{count ?? '—'}</span>
            <span className="text-xs text-gray-700">{label}</span>
          </button>
        ))}
      </section>

      <section className="bg-white rounded-lg shadow p-5 space-y-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-60">
            <label htmlFor="attempt-status" className={labelClass}>Status</label>
            <select id="attempt-status" className={inputClass} value={status} onChange={(e) => { setPage(1); setStatus(e.target.value as types.OnlinePaymentAttemptStatus | ''); }}>
              <option value="">All statuses</option>
              {types.ONLINE_PAYMENT_ATTEMPT_STATUSES.map((s) => <option key={s} value={s}>{ATTEMPT_STATUS_LABELS[s]}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm min-h-[44px]">
            <input type="checkbox" className="h-5 w-5" checked={attentionOnly} onChange={(e) => { setPage(1); setAttentionOnly(e.target.checked); }} />
            Only those needing attention
          </label>
        </div>
        {error && <Toast kind="error">{error}</Toast>}
        {notice && <Toast kind="success">{notice}</Toast>}
        {loading && !list ? (
          <p>Loading…</p>
        ) : !list || list.data.length === 0 ? (
          <p className="py-8 text-center text-gray-600">No online payments{status || attentionOnly ? ' match these filters' : ' yet'}.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Date', 'Customer', 'Invoice', 'Amount', 'Source', 'Status', 'Card', ''].map((h, i) => (
                    <th key={i} scope="col" className="px-3 py-2 text-left font-medium text-gray-600">{h || <span className="sr-only">Actions</span>}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {list.data.map((a) => {
                  const reason = attentionReason(a);
                  const canReconcile = ['RECORD_FAILED', 'CHARGED', 'PENDING', 'ERROR'].includes(a.status);
                  return (
                    <React.Fragment key={a.id}>
                      <tr className={a.needsAttention ? 'bg-red-50' : undefined} data-testid="attempt-row">
                        <td data-label="Date" className="px-3 py-2 whitespace-nowrap">{a.createdAt.slice(0, 10)}</td>
                        <td data-label="Customer" data-primary className="px-3 py-2 min-w-0 break-words"><Link className="text-blue-700 hover:underline" to={`/customers/${a.customerId}`}>{a.customerName}</Link></td>
                        <td data-label="Invoice" className="px-3 py-2">{a.invoiceId ? <Link className="text-blue-700 hover:underline" to={`/invoices/${a.invoiceId}`}>#{a.invoiceNumber}</Link> : '—'}</td>
                        <td data-label="Amount" className="px-3 py-2">{formatUsd(a.amount)}</td>
                        <td data-label="Source" className="px-3 py-2">{ATTEMPT_SOURCE_LABELS[a.source]}{a.attemptNo ? ` #${a.attemptNo}` : ''}</td>
                        <td data-label="Status" className="px-3 py-2">
                          <AttemptStatusBadge status={a.status} />
                          {a.paymentId && <Link className="ml-2 text-xs text-blue-700 hover:underline" to={`/payments/${a.paymentId}`}>Payment</Link>}
                        </td>
                        <td data-label="Card" className="px-3 py-2">{a.cardLast4 ? cardLabel({ brand: a.cardBrand, last4: a.cardLast4 }) : '—'}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-x-3 gap-y-1">
                            {canReconcile && <button type="button" className={rowActionClass} disabled={busy} onClick={() => void reconcile(a)}>Reconcile</button>}
                            {a.status === 'ERROR' && <button type="button" className={rowDangerActionClass} disabled={busy} onClick={() => setCloseFor(a.id)}>Not charged…</button>}
                            {canRefundOnlinePayments(user) && refundPendingVoid(a) && <button type="button" className={rowDangerActionClass} disabled={busy} onClick={() => setRefundFor(a.id)}>Finish refund</button>}
                          </div>
                        </td>
                      </tr>
                      {(reason || a.failureMessage || closeFor === a.id || refundFor === a.id) && (
                        <tr className={a.needsAttention ? 'bg-red-50' : undefined}>
                          <td colSpan={8} className="px-3 pb-3 text-xs text-gray-700 space-y-2">
                            {reason ? <p className="font-medium text-red-800">{reason}</p> : a.failureMessage && a.status !== 'RECORDED' && <p>{a.failureMessage}</p>}
                            {closeFor === a.id && (
                              <div role="alertdialog" aria-label="Close as not charged" className="rounded border border-red-200 bg-white p-3 space-y-2 text-sm">
                                <p>Only close this after checking the processor dashboard shows no charge for {formatUsd(a.amount)}. Autopay may then try the invoice again.</p>
                                <div className="flex flex-wrap gap-3">
                                  <button type="button" className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:bg-gray-400" disabled={busy} onClick={() => void reconcile(a, 'NOT_CHARGED')}>Close as not charged</button>
                                  <button type="button" className={secondaryButtonClass} disabled={busy} onClick={() => setCloseFor(null)}>Cancel</button>
                                </div>
                              </div>
                            )}
                            {refundFor === a.id && a.paymentId && (
                              <RefundForm amount={a.amount} finishOnly onCancel={() => setRefundFor(null)}
                                onRefund={async (r) => { await api.refundPayment(a.paymentId!, r); setRefundFor(null); setNotice('Refund finished: the payment is voided.'); await load(); }} />
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {pages > 1 && (
          <div className="flex flex-wrap justify-between items-center gap-3">
            <button type="button" className={secondaryButtonClass} disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
            <span className="text-sm text-gray-600">Page {page} of {pages}</span>
            <button type="button" className={secondaryButtonClass} disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        )}
      </section>
    </main>
  );
}
